import { StreamingTranscriber, type TurnEvent } from 'assemblyai';

import {
  type DictationEngine,
  type DictationEngineCallbacks,
  type DictationEngineOptions,
  DictationError,
  type DictationTurn,
} from '../types';

/** AssemblyAI accepts at most 100 keyterms per session. */
export const KEYTERMS_LIMIT = 100;

/**
 * Words the engine is still revising live in `words`; `transcript` only carries
 * the words it has committed. Showing both is what makes text appear as you speak.
 */
function turnText(event: TurnEvent): string {
  if (event.end_of_turn) return event.transcript;
  const words = event.words?.map(word => word.text).join(' ');
  return words || event.transcript;
}

export function toDictationTurn(event: TurnEvent): DictationTurn {
  return {
    order: event.turn_order,
    text: turnText(event),
    final: event.end_of_turn,
    formatted: event.turn_is_formatted,
  };
}

/**
 * Universal-Streaming over WebSocket, authenticated with the short-lived token
 * minted by `POST /v2/provider/transcriptions/realtime/session`. Audio goes
 * straight from the browser to AssemblyAI; the backend only mints the token.
 */
export function createAssemblyAiStreamingEngine(): DictationEngine {
  let transcriber: StreamingTranscriber | null = null;
  return {
    async connect(options: DictationEngineOptions, callbacks: DictationEngineCallbacks) {
      const session = new StreamingTranscriber({
        token: options.token,
        sampleRate: options.sampleRate,
        encoding: 'pcm_s16le',
        speechModel: 'u3-rt-pro',
        domain: 'medical-v1',
        formatTurns: true,
        keytermsPrompt: options.keyterms?.slice(0, KEYTERMS_LIMIT),
        prompt: options.prompt,
      });
      session.on('turn', event => callbacks.onTurn(toDictationTurn(event)));
      session.on('error', error => callbacks.onError(error));
      session.on('close', (code, reason) => callbacks.onClose({ code, reason }));
      try {
        await session.connect();
      } catch (error) {
        throw new DictationError(
          'connection',
          error instanceof Error && error.message
            ? `Dictation could not connect: ${error.message}`
            : 'Dictation could not connect. Check your connection and try again.',
        );
      }
      transcriber = session;
    },
    sendAudio(pcm16) {
      transcriber?.sendAudio(pcm16);
    },
    async close() {
      const session = transcriber;
      transcriber = null;
      if (session) await session.close(true);
    },
  };
}
